import apiClient, { setAppToken } from '../api/client';

/**
 * Collects the session when the installed Android app is handed one.
 *
 * The whole reason this file exists: Google refuses OAuth inside an embedded
 * WebView, so signing in from the app happens in the phone's real browser — and
 * that browser's cookies are not the app's. The finished login therefore comes
 * back as a one-time code, delivered through the app's own link (memocal://) and
 * handed to the page as `#applogin=<code>`. Here it is swapped, once, for a
 * token the app can keep.
 *
 * On a browser this never runs: no fragment, nothing to do, straight past.
 */

const RE = /(?:^|[#&])applogin=([a-f0-9]{32})(?:&|$)/;

/**
 * If the address carries a login code, spend it and store the token.
 * Returns true when a session was collected, so the caller can go and load the
 * user instead of showing the sign-in screen.
 *
 * The fragment is wiped either way, immediately. It has done its job, it must
 * not survive into the browser history, and it must not be replayed by a
 * reload — a spent code would fail and the second attempt would look like a
 * broken login rather than a repeat of a finished one.
 */
export async function collectAppSession() {
  let code = null;
  try {
    const m = RE.exec(window.location.hash || '');
    code = m && m[1];
  } catch (_) { return false; }
  if (!code) return false;

  clearHash();

  try {
    const { data } = await apiClient.post('/auth/exchange', { code });
    if (data?.token) {
      setAppToken(data.token);
      return true;
    }
  } catch (_) {
    // An expired or already-spent code is not an error worth a screen: the
    // sign-in view is about to be shown anyway, and it is the right next step.
  }
  return false;
}

function clearHash() {
  try {
    const clean = window.location.pathname + window.location.search;
    window.history.replaceState(null, '', clean);
  } catch (_) {
    try { window.location.hash = ''; } catch (_) {}
  }
}

export default collectAppSession;
